import Breadcrumbs from 'components/Layout/Breadcrumbs/Breadcrumbs';
import CommonLayout from 'components/Layout/CommonLayout';
import Webline from 'components/Layout/Webline/Webline';
import ArticleDetail from 'components/Pages/ArticleDetail/ArticleDetail';
import { Error403 } from 'components/Pages/ErrorPage/403/Error403';
import { mapBlogArticleDetail } from 'connectors/articleInterface/blogArticle/BlogArticle';
import { getFirstImage } from 'connectors/image/Image';
import {
    BlogArticleDetailQueryApi,
    BlogArticleDetailQueryDocumentApi,
    BlogArticleDetailQueryVariablesApi,
    ProductsByCatnumsDocumentApi,
    useBlogArticleDetailQueryApi,
} from 'graphql/generated';
import { createClient } from 'helpers/createClient';
import { initServerSideProps, ServerSidePropsType } from 'helpers/InitServerSideProps';
import { getIsRedirectedFromSsr } from 'helpers/isServer';
import { getUrlWithoutGetParameters } from 'helpers/parsing/urlParsing';
import { getSeoTitleAndDescriptionForFriendlyUrlPage } from 'helpers/seo/getSeoTitleAndDescriptionForFriendlyUrlPage';
import { getServerSidePropsWrapper } from 'helpers/serverSide/getServerSidePropsWrapper';
import { handleServerSideErrorResponseForFriendlyUrls } from 'helpers/serverSide/handleServerSideErrorResponseForFriendlyUrls';
import { useQueryError } from 'hooks/graphQl/UseQueryError';
import { useGtmFriendlyPageView } from 'hooks/gtm/useGtmFriendlyPageView';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { NextPageExtended } from 'types/global';
import { OgTypeEnum } from 'types/seo';
import { CombinedError, OperationResult } from 'urql';
import { checkPermisions } from 'utils/Auth/checkPermisions';
import { getSlugFromServerSideUrl, getSlugFromUrl } from 'utils/getSlugFromUrl';
import { parseCatnums } from 'utils/grapesJsParser';
import { useGtmPageViewEvent } from 'utils/Gtm/EventFactories';
import { getGtmPageInfoForFriendlyUrl } from 'utils/Gtm/Gtm';

const BlogArticleDetailPage: NextPageExtended<ServerSidePropsType> = () => {
    const router = useRouter();
    const slug = getUrlWithoutGetParameters(router.asPath);
    const [{ data: blogArticleDetailData, fetching, error }] = useBlogArticleDetailQueryApi({
        variables: { urlSlug: getSlugFromUrl(slug) },
    });

    useQueryError(error, error instanceof CombinedError);

    const mappedBlogArticleDetail = useMemo(() => {
        if (blogArticleDetailData?.blogArticle) {
            return mapBlogArticleDetail(blogArticleDetailData.blogArticle);
        }

        return null;
    }, [blogArticleDetailData?.blogArticle]);

    const blogArticleDetailSeoTitleAndDescription = mappedBlogArticleDetail
        ? getSeoTitleAndDescriptionForFriendlyUrlPage(mappedBlogArticleDetail)
        : undefined;

    const gtmBlogArticleDetailUrlPageViewEvent = useGtmPageViewEvent(
        getGtmPageInfoForFriendlyUrl(mappedBlogArticleDetail, slug, mappedBlogArticleDetail?.breadcrumb),
    );
    useGtmFriendlyPageView(gtmBlogArticleDetailUrlPageViewEvent, slug, fetching);

    const blogMainImage = mappedBlogArticleDetail?.images ? getFirstImage(mappedBlogArticleDetail.images) : undefined;

    const hasPermisions = checkPermisions(error);
    if (!hasPermisions) {
        return <Error403 />;
    }

    return (
        <CommonLayout
            showLastVisitedProducts
            isFetchingData={fetching}
            title={blogArticleDetailSeoTitleAndDescription?.title}
            description={
                blogArticleDetailData?.blogArticle?.perex ?? blogArticleDetailSeoTitleAndDescription?.description
            }
            ogType={OgTypeEnum.Article}
            ogImageUrlDefault={blogMainImage}
        >
            {mappedBlogArticleDetail?.breadcrumb && !fetching && (
                <Webline>
                    <Breadcrumbs key="breadcrumb" type="blogCategory" breadcrumb={mappedBlogArticleDetail.breadcrumb} />
                </Webline>
            )}
            {mappedBlogArticleDetail && !fetching && <ArticleDetail article={mappedBlogArticleDetail} />}
        </CommonLayout>
    );
};

BlogArticleDetailPage.layout = 'plain';

export const getServerSideProps = getServerSidePropsWrapper(
    ({ domainConfig, redisClient, ssrExchange, t }) =>
        async (context) => {
            const client = createClient({
                publicGraphqlEndpoint: domainConfig.publicGraphqlEndpoint,
                ssrExchange,
                t,
                context,
                redisClient,
            });

            const blogArticleResponse: OperationResult<BlogArticleDetailQueryApi, BlogArticleDetailQueryVariablesApi> =
                await client
                    .query(BlogArticleDetailQueryDocumentApi, {
                        urlSlug: getSlugFromServerSideUrl(context.req.url ?? '', context.req.headers),
                    })
                    .toPromise();

            const parsedCatnums = parseCatnums(blogArticleResponse.data?.blogArticle?.text ?? ['']);
            await client
                .query(ProductsByCatnumsDocumentApi, {
                    catnums: parsedCatnums,
                })
                .toPromise();

            if (getIsRedirectedFromSsr(context.req.headers)) {
                const serverSideErrorResponse = handleServerSideErrorResponseForFriendlyUrls(
                    blogArticleResponse.error?.graphQLErrors,
                    blogArticleResponse.data?.blogArticle,
                    context.res,
                );

                if (serverSideErrorResponse) {
                    return serverSideErrorResponse;
                }
            }
            const initServerSideData = await initServerSideProps({ context, domainConfig, client, ssrExchange });

            return initServerSideData;
        },
);
export default BlogArticleDetailPage;
